import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

  // Delegate clicks for gifts grid remove buttons
  this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Delegated click handler for gifts grid remove buttons
   * Ensures removing one gift removes the entire GWP group
   */
  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const removeBtn = target.closest('.cart-gifts__remove-one');
    if (!(removeBtn instanceof HTMLElement)) return;
    const key = removeBtn.getAttribute('data-line-key');
    if (!key) return;
    event.preventDefault();
    this.#removeGiftGroupByKey(key);
  };

  /**
   * Remove all GWP items that share the same _gwp_group as the item with the provided cart key.
   * @param {string} key
   */
  async #removeGiftGroupByKey(key) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      /** @type {any[]} */
  const items = Array.isArray(cart && cart.items) ? /** @type {any[]} */ (cart.items) : [];
      // Find the item by key
  const idx = items.findIndex((/** @type {any} */ it) => it && it.key === key);
      if (idx === -1) return;
      const found = items[idx];
      const props = (found && found.properties) || {};
      const isGwp = props._is_gwp === 'true';
      const group = isGwp ? String(props._gwp_group || '') : '';
      if (!isGwp || !group) {
        // Fallback: just remove this single item
        this.onLineItemRemove(idx + 1);
        return;
      }

      /** @type {number[]} */
      const lines = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        const p = (it && it.properties) || {};
        if (p._is_gwp === 'true' && String(p._gwp_group || '') === group) {
          lines.push(i + 1);
        }
      }
      await this.#multiRemove(lines);
    } catch (e) {
      // Fallback to removing only the clicked item if anything fails
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const items = Array.isArray(cart && cart.items) ? /** @type {any[]} */ (cart.items) : [];
  const idx = items.findIndex((/** @type {any} */ it) => it && it.key === key);
        if (idx !== -1) this.onLineItemRemove(idx + 1);
      } catch (_) {}
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow?.dataset.travelSizeBundleBuilderLine === 'true') {
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal. If the item is a GWP, remove all gifts in the same group.
   * @param {number} line - The line item index (1-based).
   */
  async onLineItemRemove(line) {
    const removeButton = this.#findRemoveButtonForLine(line);
    if (removeButton) {
      this.#setRemoveButtonLoading(removeButton, true);
    }

    try {
      // Fetch cart to inspect whether this line is a GWP and its group
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      /** @type {any[]} */
      const items = Array.isArray(cart && cart.items) ? cart.items : [];
      const target = items[line - 1];

      const isGwp = !!(target && target.properties && target.properties._is_gwp === 'true');
      if (isGwp) {
        // Simplified behavior per request: remove ALL GWP items currently in cart
        /** @type {number[]} */
        const lines = [];
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          const props = (it && it.properties) || {};
          if (props._is_gwp === 'true') {
            lines.push(i + 1); // 1-based
          }
        }
        await this.#multiRemove(lines);
        return;
      }

      const bundleId =
        target && target.properties && target.properties._bundle_id
          ? String(target.properties._bundle_id)
          : '';
      if (bundleId) {
        /** @type {number[]} */
        const lines = [];
        for (let i = 0; i < items.length; i++) {
          const it = items[i];
          const props = (it && it.properties) || {};
          if (String(props._bundle_id || '') === bundleId) {
            lines.push(i + 1);
          }
        }
        if (lines.length > 0) {
          this.#setBundleRemoveLoading(bundleId, true);
          await this.#multiRemove(lines);
          return;
        }
      }
    } catch (e) {
      this.#resetAllRemoveButtonsLoading();
      // Fallback to default single removal if cart inspection fails
    }

    // Default single-line removal
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Remove multiple cart lines in one UX operation, morphing once at the end.
   * @param {number[]} lines - 1-based line indices to remove.
   */
  async #multiRemove(lines) {
  if (!Array.isArray(lines) || lines.length === 0) {
    this.#resetAllRemoveButtonsLoading();
    return;
  }

  // Ensure unique, valid, and descending order to prevent index shift issues
  lines = Array.from(new Set(lines.filter((n) => typeof n === 'number' && n > 0))).sort((a, b) => b - a);
  if (lines.length === 0) return;
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const lastIndex = lines.length - 1;
    let finalResponseText = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isLast = i === lastIndex;
      const body = isLast
        ? JSON.stringify({
            line,
            quantity: 0,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          })
        : JSON.stringify({ line, quantity: 0 });

      // eslint-disable-next-line no-await-in-loop
      const respText = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
        .then((r) => r.text())
        .catch(() => null);
      if (isLast) finalResponseText = respText;

  }
    // Clear cart count cache and reload to ensure GWP logic runs fresh
    try { sessionStorage.removeItem('cart-count'); } catch(e) {}
    window.location.reload();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          resetShimmer(this);
          this.#handleCartError(line, parsedResponseText);
          this.#resetRemoveButtonLoading(this.#findRemoveButtonForLine(line));
          this.#enableCartItems();
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          return;
        }

        // Clear cart count cache and reload to ensure GWP logic runs fresh
        try { sessionStorage.removeItem('cart-count'); } catch(e) {}
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
        this.#resetRemoveButtonLoading(this.#findRemoveButtonForLine(line));
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Try to find quantity input via refs first, then fallback to direct DOM query
    let quantityInput = null;
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    
    if (quantitySelector) {
      quantityInput = quantitySelector.querySelector('input');
    }
    
    // Fallback: find input by data-cart-line attribute
    if (!quantityInput) {
      quantityInput = this.querySelector(`input[data-cart-line="${line}"]`);
    }

    if (!quantityInput) {
      console.error('Quantity input not found for line', line);
      return;
    }

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) {
      console.error('Cart item error element not found for line', line);
      return;
    }
    
    if (!(cartItemErrorContainer instanceof HTMLElement)) {
      console.error('Cart item error container not found for line', line);
      return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.#resetAllRemoveButtonsLoading();
  }

  /**
   * @param {number} line
   * @returns {HTMLButtonElement | null}
   */
  #findRemoveButtonForLine(line) {
    const button = this.querySelector(`button.cart-items__remove[on\\:click="/onLineItemRemove/${line}"]`);
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * @param {string} bundleId
   * @param {boolean} isLoading
   */
  #setBundleRemoveLoading(bundleId, isLoading) {
    if (!bundleId) {
      return;
    }
    const escapedId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(bundleId) : bundleId;
    this.querySelectorAll(`tr[data-bundle-id="${escapedId}"] .cart-items__remove`).forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        this.#setRemoveButtonLoading(button, isLoading);
      }
    });
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} isLoading
   */
  #setRemoveButtonLoading(button, isLoading) {
    button.classList.toggle('is-loading', isLoading);
    button.disabled = isLoading;
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    const spinner = button.querySelector('.cart-items__remove-spinner');
    if (spinner instanceof HTMLElement) {
      spinner.hidden = !isLoading;
    }
  }

  /**
   * @param {HTMLButtonElement | null} button
   */
  #resetRemoveButtonLoading(button) {
    if (button instanceof HTMLButtonElement) {
      this.#setRemoveButtonLoading(button, false);
    }
  }

  #resetAllRemoveButtonsLoading() {
    this.querySelectorAll('.cart-items__remove.is-loading').forEach((button) => {
      this.#resetRemoveButtonLoading(/** @type {HTMLButtonElement} */ (button));
    });
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}